import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface FaqItem {
  question: string;
  answer: string;
}

const Faq = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs: FaqItem[] = [
    {
      question: t('landing_page.faq.questions.delivery.question'),
      answer: t('landing_page.faq.questions.delivery.answer')
    },
    {
      question: t('landing_page.faq.questions.seller_fail.question'),
      answer: t('landing_page.faq.questions.seller_fail.answer')
    },
    {
      question: t('landing_page.faq.questions.withdraw.question'),
      answer: t('landing_page.faq.questions.withdraw.answer')
    },
    {
      question: t('landing_page.faq.questions.multi_account.question'),
      answer: t('landing_page.faq.questions.multi_account.answer')
    }
  ];

  return (
      <section className="w-full max-w-[1600px] mx-auto px-6 md:px-8 py-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">

          {/* Left Info Section - Adapted to LiveTrade style */}
          <div className="lg:col-span-6 lg:sticky lg:top-32">
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 bg-dark-primary rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.5)]" />
                <span className="text-light-text dark:text-dark-text font-black uppercase tracking-tighter text-3xl">
                {t('landing_page.faq.title')}
              </span>
              </div>
              <p className="text-gray-500 dark:text-slate-500 text-[13px] font-bold uppercase tracking-[0.15em] ml-1 mb-8 max-w-xs leading-relaxed">
                {t('landing_page.faq.subtitle')}
              </p>

              <Link
                  to="/faq"
                  className="inline-flex items-center justify-between w-fit gap-6 bg-white dark:bg-neutral-900/30 shadow-lg shadow-dark-primary/30 px-6 py-3 rounded-xl group hover:border-dark-primary/50 transition-all duration-300"
              >
              <span className="text-sm font-black uppercase tracking-widest text-light-text dark:text-white">
                {t('landing_page.faq.view_more')}
              </span>
                <div className="w-8 h-8 rounded-lg bg-dark-primary/10 flex items-center justify-center text-dark-primary group-hover:bg-dark-primary group-hover:text-white transition-colors">
                  <span className="transform group-hover:translate-x-0.5 transition-transform">→</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Right Accordion Section */}
          <div className="lg:col-span-6 flex flex-col gap-4">
            {faqs.map((faq, index) => (
                <div
                    key={index}
                    className={`overflow-hidden transition-all duration-300 rounded-3xl backdrop-blur-md bg-white dark:bg-neutral-900/30 shadow-lg`}
                >
                  <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between gap-6 p-6 md:p-7 text-left outline-none"
                  >
                    <div className="flex items-center gap-5">
                      <div className={`text-[10px] font-black w-6 h-6 rounded-md flex items-center justify-center border transition-all duration-300 ${
                          openFaq === index
                              ? 'bg-dark-primary border-dark-primary text-white'
                              : 'border-neutral-200 dark:border-white/10 text-gray-400'
                      }`}>
                        0{index + 1}
                      </div>
                      <span className={`text-lg font-bold tracking-tight transition-colors duration-300 ${
                          openFaq === index ? 'text-light-text dark:text-white' : 'text-gray-600 dark:text-slate-400'
                      }`}>
                    {faq.question}
                  </span>
                    </div>

                    <div className={`relative flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-500 ${
                        openFaq === index ? 'rotate-180 bg-dark-primary/10' : ''
                    }`}>
                      <motion.div
                          animate={{ rotate: openFaq === index ? 180 : 0 }}
                          className={`text-xl transition-colors ${openFaq === index ? 'text-dark-primary' : 'text-gray-400'}`}
                      >
                        {openFaq === index ? '−' : '+'}
                      </motion.div>
                    </div>
                  </button>

                  <AnimatePresence>
                    {openFaq === index && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          <div className="px-7 pb-7 pt-0 ml-11">
                            <div className="h-[1px] w-12 bg-dark-primary/30 mb-5" />
                            <p className="text-gray-500 dark:text-slate-400 leading-relaxed font-medium">
                              {faq.answer}
                            </p>
                          </div>
                        </motion.div>
                    )}
                  </AnimatePresence>
                </div>
            ))}
          </div>

        </div>
      </section>
  );
};

export default Faq;